import { useReducer, Dispatch } from 'react';
import { createAction, createReducer } from '@reduxjs/toolkit';
import TextSearchList from 'lib/TextSearchList';
import { TextSearchState } from 'types/State';

function incrementCursor(cursor: number | null): number {
  if (typeof cursor === 'number') {
    return cursor + 1;
  }
  return 0;
}

const setByTextValue = createAction<string>('setByTextValue');
const navedUp = createAction('navedUp');
const navedDown = createAction('navedDown');
const escaped = createAction('escaped');
const clickedOut = createAction('clickedkOut');
const inputChanged = createAction<string>('inputChanged');
const inputFocused = createAction('inputFocused');
const toggleDropdown = createAction('toggleDropdown');

function useTextSearchReducer(fullList: TextSearchList): [TextSearchState, Dispatch<any>] {
  const initialState: TextSearchState = {
    cursor: null,
    textInput: '',
    typedInput: '',
    list: fullList.list,
    dropDownOpen: false,
  };

  const reducer = createReducer<TextSearchState>(initialState, (builder) => {
    builder
      .addCase(setByTextValue, (state, action) => {
        return {
          ...initialState,
          textInput: action.payload,
          typedInput: action.payload,
        };
      })
      .addCase(navedUp, (state) => {
        if (state.cursor === 0) {
          return {
            ...state,
            cursor: null,
            textInput: state.typedInput,
          };
        }
        if (!state.cursor) {
          return state;
        }
        return {
          ...state,
          cursor: state.cursor - 1,
          textInput: state.list[state.cursor - 1]?.displayName,
        };
      })
      .addCase(navedDown, (state) => {
        if (state.cursor && state.cursor >= state.list.length - 1) {
          return state;
        }
        return {
          ...state,
          cursor: incrementCursor(state.cursor),
          textInput: state.list[incrementCursor(state.cursor)]?.displayName,
        };
      })
      .addCase(escaped, () => {
        return initialState;
      })
      .addCase(clickedOut, (state) => {
        return {
          ...state,
          dropDownOpen: false,
          cursor: null,
        };
      })
      .addCase(inputChanged, (_state, action) => {
        if (action.payload === '') {
          return initialState;
        }
        return {
          textInput: action.payload,
          typedInput: action.payload,
          cursor: null,
          dropDownOpen: true,
          list: fullList.filter(action.payload),
        };
      })
      .addCase(inputFocused, (state) => {
        if (state.textInput === '') {
          return state;
        }
        return {
          ...state,
          dropDownOpen: true,
          list: fullList.filter(state.textInput),
        };
      })
      .addCase(toggleDropdown, (state) => {
        return {
          ...state,
          dropDownOpen: !state.dropDownOpen,
        };
      });
  });

  return useReducer(reducer, initialState);
}

export {
  setByTextValue,
  navedUp,
  navedDown,
  escaped,
  clickedOut,
  inputChanged,
  inputFocused,
  toggleDropdown,
};

export default useTextSearchReducer;
